/**
 * Mirrors the backend `StateEnum`: the 50 states, DC, the territories and freely
 * associated states, and the three armed-forces codes. The API accepts every one
 * of these, so every one is offered.
 *
 * This is the single declaration of the set — any other screen that takes a state
 * imports it rather than writing its own list.
 */
export type StateAbbreviation =
  | 'AA' | 'AE' | 'AK' | 'AL' | 'AP' | 'AR' | 'AS' | 'AZ'
  | 'CA' | 'CO' | 'CT' | 'DC' | 'DE'
  | 'FL' | 'FM' | 'GA' | 'GU' | 'HI'
  | 'IA' | 'ID' | 'IL' | 'IN' | 'KS' | 'KY' | 'LA'
  | 'MA' | 'MD' | 'ME' | 'MH' | 'MI' | 'MN' | 'MO' | 'MP' | 'MS' | 'MT'
  | 'NC' | 'ND' | 'NE' | 'NH' | 'NJ' | 'NM' | 'NV' | 'NY'
  | 'OH' | 'OK' | 'OR' | 'PA' | 'PR' | 'PW' | 'RI'
  | 'SC' | 'SD' | 'TN' | 'TX' | 'UT'
  | 'VA' | 'VI' | 'VT' | 'WA' | 'WI' | 'WV' | 'WY'

/** Sorted for scanning in a dropdown rather than grouped by kind. */
export const US_STATE_OPTIONS: readonly StateAbbreviation[] = [
  'AA', 'AE', 'AK', 'AL', 'AP', 'AR', 'AS', 'AZ',
  'CA', 'CO', 'CT', 'DC', 'DE',
  'FL', 'FM', 'GA', 'GU', 'HI',
  'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA',
  'MA', 'MD', 'ME', 'MH', 'MI', 'MN', 'MO', 'MP', 'MS', 'MT',
  'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY',
  'OH', 'OK', 'OR', 'PA', 'PR', 'PW', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT',
  'VA', 'VI', 'VT', 'WA', 'WI', 'WV', 'WY',
]

/**
 * The zones a US practice can be in, as canonical IANA names.
 *
 * The API takes `timezone` as a free string, so this list is the only thing
 * keeping the stored value canonical — and it has to be. Downstream code turns
 * the stored string straight into a `ZoneInfo`, and treats a missing one as
 * Pacific, so a non-canonical or absent zone shows up as silently wrong
 * appointment and reminder times rather than as an error.
 */
export const TIMEZONE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Phoenix', label: 'Mountain — no DST (Arizona)' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
  { value: 'America/Puerto_Rico', label: 'Atlantic' },
]

/**
 * Body accepted by POST /api/v1/locations/{org_id}.
 *
 * Mirrors what the request schema actually enforces rather than what the columns
 * allow: the address fields are all nullable in the database, but the endpoint
 * declares every one of these required except `state` and `suite`, and rejects a
 * blank or absent value with a 422.
 */
export interface CreateLocationRequest {
  display_name: string
  street: string
  city: string
  postal_code: string
  primary_phone: string
  timezone: string
  state?: StateAbbreviation
  suite?: string
}

/**
 * The subset of a location the app reads, as every endpoint returning one gives it.
 *
 * The list, the detail, and the create response all carry these fields, so reading
 * an organization's locations is one request no matter how many it has.
 *
 * The detail endpoint returns considerably more (NPI, Kno2, insurance, and fax
 * fields); they are omitted until something here needs them. Which location is
 * primary comes from the organization's `primary_location_id`, not the location.
 */
export interface LocationDetail {
  id: string
  display_name: string
  enabled: boolean
  street: string | null
  city: string | null
  state: StateAbbreviation | null
  postal_code: string | null
  suite: string | null
  primary_phone: string | null
  timezone: string | null
}

/**
 * The location fields a form collects, shared by every surface that creates one.
 *
 * Distinct from `CreateLocationRequest`: this is what the inputs hold, so the
 * two select fields carry `''` for "nothing chosen yet" and every field is a
 * string. Mapping to the request is `buildLocationRequest`'s job.
 */
export interface LocationFormValues {
  location_name: string
  street: string
  suite: string
  city: string
  state: StateAbbreviation | ''
  postal_code: string
  primary_phone: string
  timezone: string
}

export const defaultLocationFormValues: LocationFormValues = {
  location_name: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  postal_code: '',
  primary_phone: '',
  timezone: '',
}

/** Enforced by the backend request schema; mirrored here so the UI can warn first. */
export const LOCATION_NAME_MAX_LENGTH = 35
